import styled from 'styled-components'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { OrderType, OwnersSortByType, TimeFilterType } from '@/shared/backend/types/leaderboard'

import { Option, ReactSelect } from 'components/ReactSelect/ReactSelect'
import { useLeaderboardOwners } from '../hooks/useLeaderboardOwners'
import { Banner } from 'components/Banner/Banner'
import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'
import { paginate } from 'components/Pagination/Pagination'
import { PAGE_SIZE, timeFilterOptions, walletsSortMobileOptions } from '../const'
import { SimpleAutocomplete } from 'components/Autocomplete/SimpleAutocomplete'
import { WalletsTable } from './WalletsTable'
import { WalletsList } from './WalletsList'
import { WalletFilters } from './WalletsFilters'
import { usePostHogEntityViewed } from 'utils/usePostHogEntityViewed'

const Container = styled.div`
  margin-top: 32px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin-top: 20px;
  }
`

const ContainerControls = styled.div<{ $isLoading: boolean }>`
  ${p =>
    p.$isLoading &&
    `
  opacity: 0.5;
  pointer-events: none;
  `}
`

const WalletsControls = styled.div<{ $isLoading: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${p =>
    p.$isLoading &&
    `
  opacity: 0.5;
  pointer-events: none;
  `}
`

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FILTERS_KEY = 'leaderboard-wallets-filters'

const SORT_BY_KEY = 'sort-by'
const SORT_ORDER_KEY = 'sort-order'
const TIME_FILTER_KEY = 'time-filter'

const ORDER_TYPES: OrderType[] = ['asc', 'desc']
const SORT_ORDER_TYPES: OwnersSortByType[] = [
  'totalPositions',
  'totalPnl',
  'totalReturnOnEquity',
  'openEquity',
  'totalHodlBenchmarkPnl',
]
const TIME_FILTER_TYPES = timeFilterOptions.map(o => o.value)

export const Wallets = () => {
  usePostHogEntityViewed('leaderboard:wallets_view')

  const [sortBy, setSortBy] = useState<OwnersSortByType>('totalPnl')
  const [sortOrder, setSortOrder] = useState<OrderType>('desc')
  const [selectedWallets, setSelectedWallets] = useState<string[]>([])
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(timeFilterOptions[0])

  const [currentPage, setCurrentPage] = useState(1)

  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    const savedFilters = localStorage.getItem(FILTERS_KEY)
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters)
      const sortByLS = parsedFilters[SORT_BY_KEY]
      if (sortByLS && SORT_ORDER_TYPES.indexOf(sortByLS) !== -1) {
        setSortBy(sortByLS)
      }
      const sortOrderLS = parsedFilters[SORT_ORDER_KEY]
      if (sortOrderLS && ORDER_TYPES.indexOf(sortOrderLS) !== -1) {
        setSortOrder(sortOrderLS)
      }
      const timeFilterLs = parsedFilters[TIME_FILTER_KEY]
      if (timeFilterLs && TIME_FILTER_TYPES.indexOf(timeFilterLs) !== -1) {
        const timeFilterOption = timeFilterOptions.find(o => o.value === timeFilterLs)
        if (timeFilterOption) {
          setSelectedTimeFilter(timeFilterOption)
        }
      }
    }
    setHydrated(true)
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [sortBy, sortOrder, selectedTimeFilter, selectedWallets])

  useEffect(() => {
    if (!hydrated) {
      return
    }
    localStorage.setItem(
      FILTERS_KEY,
      JSON.stringify({
        [SORT_BY_KEY]: sortBy,
        [SORT_ORDER_KEY]: sortOrder,
        [TIME_FILTER_KEY]: selectedTimeFilter.value,
      })
    )
  }, [hydrated, sortBy, sortOrder, selectedTimeFilter])

  const isMobile = useIsMobile()

  const { data, isLoading, isError } = useLeaderboardOwners(
    hydrated,
    sortBy,
    sortOrder,
    selectedTimeFilter.value as TimeFilterType
  )

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page)
      if (isMobile) {
        window.scrollTo(0, 0)
      }
    },
    [isMobile]
  )

  const handleSelectTimeFilterChange = useCallback((newValue: Option) => {
    setSelectedTimeFilter(newValue)
  }, [])

  const handleSubmitFilters = useCallback((newValue: Option, selectedSort: string) => {
    const sortOption = selectedSort.split(' ')[0] as OwnersSortByType
    const isDesc = selectedSort.indexOf('desc') !== -1
    if (isDesc) {
      setSortOrder('desc')
    } else {
      setSortOrder('asc')
    }
    setSortBy(sortOption)
    setSelectedTimeFilter(newValue)
  }, [])

  const handleWalletIdsChange = useCallback((values: string[]) => {
    setSelectedWallets(values)
  }, [])

  const handleColumnClick = useCallback(
    (columnKey: OwnersSortByType) => {
      if (columnKey === sortBy) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
      } else {
        setSortOrder('desc')
      }
      setSortBy(columnKey)
    },
    [sortBy, sortOrder]
  )

  const filteredData = useMemo(() => {
    if (!selectedWallets.length) {
      return data
    }
    return data?.filter(d => selectedWallets.indexOf(d.ownerId) !== -1)
  }, [selectedWallets, data])

  const paginatedItems = paginate(filteredData || [], PAGE_SIZE, currentPage)

  const totalPages = Math.ceil((filteredData || []).length / PAGE_SIZE)

  if (isError) {
    return (
      <Container>
        <Banner variant="warning">
          <Typography variant={isMobile ? 'caption2' : 'body'} fontWeight="semiBold">
            Data not available
          </Typography>
        </Banner>
      </Container>
    )
  }

  return (
    <Container>
      {isMobile ? (
        <ContainerControls $isLoading={isLoading}>
          <TitleContainer style={{ marginBottom: 12 }}>
            <Typography variant="label1" fontWeight="semiBold">
              Wallets
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" style={{ marginRight: 8 }}>
                Sort: {walletsSortMobileOptions.find(o => o.value === `${sortBy} (${sortOrder})`)?.label}
              </Typography>
              <WalletFilters
                initialTimeFilter={selectedTimeFilter}
                onSubmit={handleSubmitFilters}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </div>
          </TitleContainer>
          <SimpleAutocomplete
            options={(data || []).map(d => d.ownerId)}
            value={selectedWallets}
            onChange={handleWalletIdsChange}
          />
        </ContainerControls>
      ) : (
        <WalletsControls $isLoading={isLoading}>
          <SimpleAutocomplete
            options={(data || []).map(d => d.ownerId)}
            value={selectedWallets}
            onChange={handleWalletIdsChange}
          />
          <ReactSelect
            options={timeFilterOptions}
            selectedOption={selectedTimeFilter}
            onSelect={handleSelectTimeFilterChange}
          />
        </WalletsControls>
      )}
      {isMobile ? (
        <WalletsList
          paginatedItems={paginatedItems}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          isLoading={isLoading}
        />
      ) : (
        <WalletsTable
          sortBy={sortBy}
          handleColumnClick={handleColumnClick}
          sortOrder={sortOrder}
          paginatedItems={paginatedItems}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          isLoading={isLoading}
        />
      )}
    </Container>
  )
}
